import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Admin } from '../types';
import { adminAuthService } from '../services/auth';
import { logger } from '../utils';
import { setUserContext, clearUserContext } from '../config/sentry';

interface AuthContextType {
  admin: Admin | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (token) {
          const adminData = await adminAuthService.verifyToken(token);
          setAdmin(adminData);
          // Set Sentry user context
          setUserContext({
            id: adminData.id,
            email: adminData.email,
            name: adminData.name,
          });
        }
      } catch (error) {
        logger.error('Token verification failed', error, { component: 'AuthContext', action: 'initAuth' });
        localStorage.removeItem('admin_token');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { admin: adminData, token } = await adminAuthService.login(email, password);
      localStorage.setItem('admin_token', token);
      setAdmin(adminData);
      // Set Sentry user context
      setUserContext({
        id: adminData.id,
        email: adminData.email,
        name: adminData.name,
      });
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setAdmin(null);
    // Clear Sentry user context
    clearUserContext();
  };

  const value = {
    admin,
    isLoading,
    login,
    logout,
    isAuthenticated: !!admin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
